import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6df0aaff40lgbk6dc5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section id="contact" style={{ scrollMarginTop: '80px' }} className="pt-20 pb-20 px-6 min-h-screen bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-ocean-mid/10 to-sunset-coral/10 px-4 py-1.5 rounded-full mb-4 border border-ocean-mid/20">
              <i className="ri-mail-line text-ocean-mid text-sm"></i>
              <span className="text-xs font-semibold text-ocean-mid uppercase tracking-wide">Get in Touch</span>
            </div>
            <h4>
              <a href="#contact" className="text-2xl md:text-3xl font-bold text-ocean-mid font-serif">
                Let&apos;s Start Working Together
              </a>
            </h4>
            <p className="text-xs text-gray-700 leading-relaxed mt-3 mb-5">
              Ready to streamline your financial processes? Contact us today and discover how BB Beyhan Balance can support your business growth.
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-ocean-mid/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-ocean-mid text-base"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-xs">Email</div>
                  <a href="mailto:info@beyhanbalance.com" className="text-ocean-mid hover:text-sunset-coral transition-colors cursor-pointer text-xs">
                    info@beyhanbalance.com
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-sunset-coral/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-sunset-coral text-base"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-xs">Phone</div>
                  <a href="tel:+31685297511" className="text-ocean-mid hover:text-sunset-coral transition-colors cursor-pointer text-xs">
                    +31 6 85297511
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-ocean-mid/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-ocean-mid text-base"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-xs">Address</div>
                  <p className="text-xs text-gray-700">Amsterdam 1065 LH</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-9 h-9 bg-sunset-coral/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-sunset-coral text-base"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-xs mb-1">Service Options</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-700">🌍 Remote</span>
                    <span className="text-xs text-gray-700">🇳🇱 Netherlands On-site</span>
                    <span className="text-xs text-gray-700">🇹🇷 Turkey On-site</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 p-4 bg-gradient-to-br from-ocean-mid/5 to-sunset-coral/5 rounded-xl border border-ocean-mid/15">
              <div className="flex items-start space-x-3">
                <i className="ri-lightbulb-line text-sunset-coral text-lg flex-shrink-0"></i>
                <div>
                  <div className="font-semibold text-gray-800 text-xs mb-1">Why Choose BB?</div>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    Professional expertise combined with personal dedication. I guide you at every step with reliable, empathetic support that keeps your business running smoothly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-ocean-pale/40 to-sunset-warm/40 rounded-2xl p-6 shadow-xl border border-ocean-mid/15">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-gray-800 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/20 outline-none transition-all text-xs bg-white"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-800 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/20 outline-none transition-all text-xs bg-white"
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="company" className="block text-xs font-semibold text-gray-800 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/20 outline-none transition-all text-xs bg-white"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-gray-800 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/20 outline-none transition-all text-xs bg-white"
                    placeholder="+1 234 567 890"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-xs font-semibold text-gray-800 mb-1">
                  Service Interested In
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/20 outline-none transition-all text-xs cursor-pointer bg-white"
                >
                  <option value="">Select a service</option>
                  <option value="Bookkeeping">Bookkeeping</option>
                  <option value="VAT Filing & Review">VAT Filing &amp; Review</option>
                  <option value="Financial Control">Financial Control</option>
                  <option value="Financial Accounting Support">Financial Accounting Support</option>
                  <option value="Accounts Payable & Receivable">Accounts Payable &amp; Receivable</option>
                  <option value="Bank Monitoring & Payment Processing">Bank Monitoring &amp; Payment Processing</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-gray-800 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-ocean-mid focus:ring-2 focus:ring-ocean-mid/20 outline-none transition-all resize-none text-xs bg-white"
                  placeholder="Tell us about your needs..."
                ></textarea>
                <div className="text-[10px] text-gray-500 mt-0.5 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-sunset-warm border border-sunset-coral/30 rounded-lg p-3 flex items-center space-x-2">
                  <i className="ri-checkbox-circle-line text-sunset-coral text-base"></i>
                  <span className="text-xs text-gray-800">Thank you! Your message has been sent successfully.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                  <i className="ri-error-warning-line text-red-500 text-base"></i>
                  <span className="text-xs text-red-600">Something went wrong. Please try again.</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-ocean-mid text-white px-6 py-3 rounded-lg hover:shadow-xl hover:bg-ocean-deep transition-all duration-300 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <i className="ri-send-plane-fill"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
